/**
 * Configuración del dispositivo.
 *
 * Esto NO se sincroniza, a propósito: es de cada aparato. Si el secreto se
 * sincronizara, cambiarlo en el celular lo cambiaría en el PC y quedarías
 * fuera de los dos a la vez.
 *
 * Vive en localStorage y no en la base de datos porque hace falta ANTES de
 * poder hablar con el servidor — es el huevo antes que la gallina.
 */

import { TABLAS_SINCRONIZADAS } from './db.js';

const LLAVE = 'config_finanzas';

/**
 * La dirección de la API se deduce sola.
 *
 * Como la app y el backend quedaron en el mismo dominio, la API siempre está
 * en /api de donde sea que se esté sirviendo la app. Eso significa un campo
 * menos que llenar y un error menos que cometer: no hay forma de escribir mal
 * una dirección que nunca se escribe.
 *
 * En desarrollo (localhost:5173) no hay backend, así que queda vacía y la app
 * simplemente no sincroniza. Que es lo correcto: guardar en el dispositivo
 * funciona igual.
 */
function urlPorDefecto() {
  if (typeof location === 'undefined') return '';
  const esDesarrollo = /^(localhost|127\.|192\.168\.|\[::1\])/.test(location.hostname);
  return esDesarrollo ? '' : `${location.origin}/api`;
}

const VACIA = {
  url: '',
  secreto: '',
  sincronizarAutomatico: true,
  ocrActivado: false,      // se prende cuando la key de Gemini esté puesta
  pinActivado: false,
};

export function leer() {
  try {
    const guardado = JSON.parse(localStorage.getItem(LLAVE) || '{}');
    return { ...VACIA, url: urlPorDefecto(), ...guardado };
  } catch {
    // localStorage puede fallar en ventana privada o con las cookies
    // bloqueadas. Devolver valores vacíos deja la app usable sin sincronizar.
    return { ...VACIA, url: urlPorDefecto() };
  }
}

export function guardar(cambios) {
  const nueva = { ...leer(), ...cambios };
  try {
    localStorage.setItem(LLAVE, JSON.stringify(nueva));
  } catch {
    // Si no se puede guardar, la app sigue funcionando en memoria hasta que
    // se cierre. Es mejor que romperse.
  }
  return nueva;
}

export function estaConfigurada(cfg = leer()) {
  return Boolean(cfg.url && cfg.secreto);
}

/**
 * Exporta todo a un archivo para guardar aparte.
 *
 * Esta es la red de seguridad de verdad. El navegador puede borrar sus datos
 * bajo presión de almacenamiento, y aunque la app pide permiso permanente, no
 * siempre se lo conceden. Un archivo que tú controlas no depende de eso.
 */
export async function exportar(db) {
  // La lista sale de db.js, la MISMA que usa la sincronización. Antes estaba
  // escrita a mano aquí, y cuando se agregó la tabla de contratos nadie se
  // acordó de sumarla: el respaldo salía sin contratos y sin avisar.
  const datos = {};
  for (const t of TABLAS_SINCRONIZADAS) {
    datos[t] = await db[t].toArray();
  }

  return {
    version: 2,
    exportado_en: new Date().toISOString(),
    dispositivo: localStorage.getItem('id_dispositivo'),
    conteos: Object.fromEntries(Object.entries(datos).map(([k, v]) => [k, v.length])),
    datos,
  };
}

/**
 * Empezar de nuevo: borra todo y deja la app como recién instalada.
 *
 * Borra en los DOS lados. Si solo se borrara aquí, la siguiente
 * sincronización bajaría todo otra vez desde el servidor y parecería que los
 * datos resucitan solos.
 *
 * Devuelve qué se pudo borrar y qué no, en vez de fallar en silencio: es peor
 * creer que quedó limpio y que no.
 */
export async function borrarTodo(db, api = null) {
  const resultado = { local: false, servidor: false, nota: null };

  // Primero el servidor. Si falla, se aborta: borrar solo local dejaría al
  // usuario creyendo que empezó de cero cuando no.
  if (api?.configurada) {
    try {
      await api.llamar('reiniciar', { confirmacion: 'BORRAR TODO' });
      resultado.servidor = true;
    } catch (e) {
      resultado.nota = `No se pudo borrar en el servidor: ${e.message}. ` +
        'No se borró nada, para no dejar los dos lados distintos.';
      return resultado;
    }
  } else {
    resultado.nota = 'La sincronización no está configurada, así que solo se borró este dispositivo.';
  }

  // Ahora sí, lo local. Se cierra y se elimina la base entera: vaciar tabla
  // por tabla dejaría los índices y la marca de última sincronización.
  db.close();
  await new Promise((resolver) => {
    const req = indexedDB.deleteDatabase('educacion_financiera');
    req.onsuccess = resolver;
    req.onerror = resolver;
    req.onblocked = resolver;
  });

  // Y las marcas locales que no viven en la base.
  try {
    localStorage.removeItem('avisos_cerrados');
    // El id del dispositivo se conserva: si cambiara, el servidor trataría
    // este celular como uno nuevo y se devolvería a sí mismo sus cambios.
  } catch { /* sin localStorage tampoco pasa nada */ }

  resultado.local = true;
  return resultado;
}

/** Dispara la descarga del respaldo. */
export function descargar(contenido, nombre) {
  const blob = new Blob([JSON.stringify(contenido, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  // Liberar el objeto: si no, el navegador se queda con la copia en memoria
  // hasta que se recargue la página.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
